import ExcelJSModule from 'exceljs/dist/exceljs.min.js';
import moment from 'moment';

const ExcelJS = ExcelJSModule?.Workbook ? ExcelJSModule : (ExcelJSModule.default || ExcelJSModule);

const EXCEL_MIME =
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

const HEADER_FILL = {
  type: 'pattern',
  pattern: 'solid',
  fgColor: { argb: 'FFB71C1C' }
};

const HEADER_FONT = {
  bold: true,
  color: { argb: 'FFFFFFFF' },
  size: 9
};

const THIN_BORDER = (() => {
  const edge = { style: 'thin', color: { argb: 'FFBDBDBD' } };
  return { top: edge, left: edge, bottom: edge, right: edge };
})();

const EMPTY_TABLE_LABEL = 'No data available';

export const EMPLOYEE_LEGEND = [
  'M = Mandatory',
  'D = Development',
  'C = Completed',
  'NC = Not Completed',
  'ED = Expired'
];

export function slugifyFilenamePart(value, fallback = 'All') {
  const text = String(value == null || value === '' ? fallback : value).trim();
  const slug = text.replace(/\s+/g, '_').replace(/[^\w.-]/g, '');
  return slug || fallback;
}

export function buildDashboardReportFilename(segments = []) {
  const stamp = moment().format('DDMMYYYYHHmm');
  const middle = segments
    .map((segment) => slugifyFilenamePart(segment))
    .filter(Boolean)
    .join('_');
  return middle
    ? `Dashboard_Report_${middle}_${stamp}.xlsx`
    : `Dashboard_Report_${stamp}.xlsx`;
}

export function formatReportPeriod(dateRange) {
  if (!dateRange?.start && !dateRange?.end) {
    return '-';
  }
  const start = dateRange?.start
    ? moment(dateRange.start).format('DD/MM/YYYY')
    : '-';
  const end = dateRange?.end ? moment(dateRange.end).format('DD/MM/YYYY') : '-';
  return `${start} - ${end}`;
}

export function formatTrainingTypeLabel(value) {
  const map = {
    all: 'All',
    mandatory: 'Mandatory',
    development: 'Development'
  };
  return map[value] || value || 'All';
}

export function formatActualFilterLabel(value) {
  const map = {
    C: 'Completed',
    NC: 'Not Completed',
    ED: 'Expired'
  };
  return map[value] || 'All Actual';
}

export async function downloadExcelWorkbook(workbook, filename) {
  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], { type: EXCEL_MIME });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  setTimeout(() => {
    link.remove();
    window.URL.revokeObjectURL(url);
  }, 100);
}

function styleHeaderCell(cell) {
  cell.font = HEADER_FONT;
  cell.fill = HEADER_FILL;
  cell.alignment = { vertical: 'middle', horizontal: 'center', wrapText: true };
  cell.border = THIN_BORDER;
}

function styleDataCell(cell, { align = 'center' } = {}) {
  cell.alignment = { vertical: 'middle', horizontal: align, wrapText: true };
  cell.border = THIN_BORDER;
  cell.font = { size: 9 };
}

function applyHeaderStyleRange(sheet, startRow, endRow, startCol, endCol) {
  for (let col = startCol; col <= endCol; col += 1) {
    for (let row = startRow; row <= endRow; row += 1) {
      styleHeaderCell(sheet.getCell(row, col));
    }
  }
}

function writeEmptyTableRow(sheet, row, totalCols) {
  sheet.mergeCells(row, 1, row, totalCols);
  sheet.getCell(row, 1).value = EMPTY_TABLE_LABEL;
  sheet.getCell(row, 1).alignment = { horizontal: 'center', vertical: 'middle' };
  sheet.getCell(row, 1).font = { italic: true, size: 10 };
  sheet.getCell(row, 1).border = THIN_BORDER;
}

function formatPercentCell(value) {
  const amount = value || 0;
  return `${amount}%`;
}

function writeInfoSheet(workbook, { metaRows = [], legend = [] } = {}) {
  if (!metaRows.length && !legend.length) {
    return;
  }

  const sheet = workbook.addWorksheet('Info');
  let row = 1;

  metaRows.forEach((item) => {
    sheet.getCell(row, 1).value = item.label;
    sheet.getCell(row, 1).font = { bold: true, size: 10 };
    sheet.mergeCells(row, 2, row, 4);
    sheet.getCell(row, 2).value = item.value == null || item.value === '' ? '-' : item.value;
    sheet.getCell(row, 2).font = { size: 10 };
    row += 1;
  });

  if (legend.length) {
    row += 1;
    sheet.getCell(row, 1).value = 'Legend';
    sheet.getCell(row, 1).font = { bold: true, size: 10 };
    row += 1;
    legend.forEach((item) => {
      sheet.getCell(row, 1).value = item;
      sheet.getCell(row, 1).font = { size: 10 };
      row += 1;
    });
  }

  sheet.getColumn(1).width = 22;
  sheet.getColumn(2).width = 40;
}

function createWorkbook(sheetName) {
  const workbook = new ExcelJS.Workbook();
  workbook.creator = 'UGEMS University';
  workbook.created = new Date();
  const sheet = workbook.addWorksheet(sheetName);
  return { workbook, sheet };
}

/**
 * Mirrors Detail / Mitra table:
 * NO | COMPANY NAME | COURSES (course: PLAN, ACTUAL, GOALS) | TOTAL
 */
export async function exportCompanyMatrixExcel({
  sheetName = 'Dashboard Detail',
  filename,
  metaRows = [],
  courses = [],
  companies = [],
  startNo = 1
}) {
  const { workbook, sheet } = createWorkbook(sheetName);
  const courseCount = courses.length;
  const totalCol = 2 + courseCount * 3 + 1;
  const header1 = 1;

  sheet.getCell(header1, 1).value = 'NO';
  sheet.mergeCells(header1, 1, header1 + 2, 1);
  sheet.getCell(header1, 2).value = 'COMPANY NAME';
  sheet.mergeCells(header1, 2, header1 + 2, 2);

  if (courseCount > 0) {
    sheet.getCell(header1, 3).value = 'COURSES';
    sheet.mergeCells(header1, 3, header1, 2 + courseCount * 3);
  }

  sheet.getCell(header1, totalCol).value = 'TOTAL';
  sheet.mergeCells(header1, totalCol, header1 + 2, totalCol);

  courses.forEach((course, index) => {
    const startCol = 3 + index * 3;
    sheet.getCell(header1 + 1, startCol).value = course.code || course.name || '';
    sheet.mergeCells(header1 + 1, startCol, header1 + 1, startCol + 1);
    sheet.getCell(header1 + 1, startCol + 2).value = 'GOALS';
    sheet.mergeCells(header1 + 1, startCol + 2, header1 + 2, startCol + 2);
    sheet.getCell(header1 + 2, startCol).value = 'PLAN';
    sheet.getCell(header1 + 2, startCol + 1).value = 'ACTUAL';
  });

  applyHeaderStyleRange(sheet, header1, header1 + 2, 1, totalCol);

  const dataStart = header1 + 3;
  if (!companies.length) {
    writeEmptyTableRow(sheet, dataStart, totalCol);
  } else {
    companies.forEach((company, index) => {
      const row = dataStart + index;
      sheet.getCell(row, 1).value = startNo + index;
      sheet.getCell(row, 2).value = company.name || '';
      courses.forEach((course, courseIndex) => {
        const startCol = 3 + courseIndex * 3;
        const competency = company.competencies?.[course.code] || {};
        sheet.getCell(row, startCol).value = competency.plan || 0;
        sheet.getCell(row, startCol + 1).value = competency.actual || 0;
        sheet.getCell(row, startCol + 2).value = formatPercentCell(competency.goals);
      });
      sheet.getCell(row, totalCol).value = formatPercentCell(company.total);

      styleDataCell(sheet.getCell(row, 1));
      styleDataCell(sheet.getCell(row, 2), { align: 'left' });
      for (let col = 3; col <= totalCol; col += 1) {
        styleDataCell(sheet.getCell(row, col));
      }
    });
  }

  sheet.getColumn(1).width = 8;
  sheet.getColumn(2).width = 32;
  courses.forEach((_, index) => {
    sheet.getColumn(3 + index * 3).width = 12;
    sheet.getColumn(4 + index * 3).width = 12;
    sheet.getColumn(5 + index * 3).width = 12;
  });
  sheet.getColumn(totalCol).width = 12;
  sheet.getRow(header1 + 1).height = 28;
  sheet.views = [{ state: 'frozen', xSplit: 2, ySplit: header1 + 2 }];

  writeInfoSheet(workbook, { metaRows });
  await downloadExcelWorkbook(workbook, filename);
}

/**
 * Mirrors Employee Detail table:
 * NO | NAME | POSITION | COURSES (course: PLAN, ACTUAL) | TOTAL
 */
export async function exportEmployeeMatrixExcel({
  sheetName = 'Employee Detail',
  filename,
  metaRows = [],
  courses = [],
  employees = [],
  startNo = 1
}) {
  const { workbook, sheet } = createWorkbook(sheetName);
  const courseCount = courses.length;
  const totalCol = 3 + courseCount * 2 + 1;
  const header1 = 1;

  sheet.getCell(header1, 1).value = 'NO';
  sheet.mergeCells(header1, 1, header1 + 2, 1);
  sheet.getCell(header1, 2).value = 'NAME';
  sheet.mergeCells(header1, 2, header1 + 2, 2);
  sheet.getCell(header1, 3).value = 'POSITION';
  sheet.mergeCells(header1, 3, header1 + 2, 3);

  if (courseCount > 0) {
    sheet.getCell(header1, 4).value = 'COURSES';
    sheet.mergeCells(header1, 4, header1, 3 + courseCount * 2);
  }

  sheet.getCell(header1, totalCol).value = 'TOTAL';
  sheet.mergeCells(header1, totalCol, header1 + 2, totalCol);

  courses.forEach((course, index) => {
    const startCol = 4 + index * 2;
    sheet.getCell(header1 + 1, startCol).value = course.code || course.name || '';
    sheet.mergeCells(header1 + 1, startCol, header1 + 1, startCol + 1);
    sheet.getCell(header1 + 2, startCol).value = 'PLAN';
    sheet.getCell(header1 + 2, startCol + 1).value = 'ACTUAL';
  });

  applyHeaderStyleRange(sheet, header1, header1 + 2, 1, totalCol);

  const dataStart = header1 + 3;
  if (!employees.length) {
    writeEmptyTableRow(sheet, dataStart, totalCol);
  } else {
    employees.forEach((employee, index) => {
      const row = dataStart + index;
      sheet.getCell(row, 1).value = startNo + index;
      sheet.getCell(row, 2).value = employee.name || '';
      sheet.getCell(row, 3).value = employee.position || '';
      courses.forEach((course, courseIndex) => {
        const startCol = 4 + courseIndex * 2;
        const competency = employee.competencies?.[course.code] || {};
        sheet.getCell(row, startCol).value = competency.plan || '-';
        sheet.getCell(row, startCol + 1).value = competency.actual || '-';
      });
      sheet.getCell(row, totalCol).value = formatPercentCell(employee.total);

      styleDataCell(sheet.getCell(row, 1));
      styleDataCell(sheet.getCell(row, 2), { align: 'left' });
      styleDataCell(sheet.getCell(row, 3), { align: 'left' });
      for (let col = 4; col <= totalCol; col += 1) {
        styleDataCell(sheet.getCell(row, col));
      }
    });
  }

  sheet.getColumn(1).width = 8;
  sheet.getColumn(2).width = 28;
  sheet.getColumn(3).width = 28;
  courses.forEach((_, index) => {
    sheet.getColumn(4 + index * 2).width = 14;
    sheet.getColumn(5 + index * 2).width = 14;
  });
  sheet.getColumn(totalCol).width = 12;
  sheet.getRow(header1 + 1).height = 28;
  sheet.views = [{ state: 'frozen', xSplit: 3, ySplit: header1 + 2 }];

  writeInfoSheet(workbook, { metaRows, legend: EMPLOYEE_LEGEND });
  await downloadExcelWorkbook(workbook, filename);
}
